// HomePage.js - A scalable React component for the ChristensenKirtis Publishing homepage.
// This leverages React's component-based architecture for longevity and easy growth—add AI-driven features like dynamic recommendations later without major rewrites.
// Designed to integrate seamlessly with Django backend via API calls (e.g., fetch blog posts or announcements).
// For portability: Use environment variables for API endpoints to avoid server lock-in; deploy anywhere with Docker.
// Prototype locally: Run 'npm start' in your frontend folder; test API integration with Django's runserver.

import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../api';
import './HomePage.css'; // Import CSS for styling (create this file for responsive, scalable design).


const HomePage = () => {
  // State for dynamic content—fetched from Django API for scalability.
  const [announcements, setAnnouncements] = useState([]); // Billboard data.
  const [blogPosts, setBlogPosts] = useState([]); // Blog posts list.
  const [showAllBlogs, setShowAllBlogs] = useState(false); // Toggle to show all blogs.
  const blogSectionRef = useRef(null); // Reference to blog section for scrolling.

  useEffect(() => {
    // Fetch data from Django backend—use async/await for clean, future-proof code.
    // Leverage AI: In production, integrate ML for personalized announcements.
    const fetchData = async () => {
      try {
        // Use centralized API URL for portability
        const apiUrl = `${API_URL}/api/`; // Django API base.
        
        // Fetch announcements.
        const annRes = await fetch(`${apiUrl}announcements/`);
        const annData = await annRes.json();
        setAnnouncements(annData);

        // Fetch blog posts.
        const blogRes = await fetch(`${apiUrl}blog-posts/`);
        const blogData = await blogRes.json();
        setBlogPosts(blogData);
      } catch (error) {
        console.error('Error fetching data:', error);
        // Fallback static data for testing resilience.
        setAnnouncements([{ id: 1, text: 'New Release: Exciting Ebook Now Available!' }]);
        setBlogPosts([
          { id: 1, title: 'Blog Post 1', excerpt: 'A short summary...', date: '2025-12-04' },
          { id: 2, title: 'Blog Post 2', excerpt: 'Another insight...', date: '2025-11-28' },
        ]);
      }
    };
    fetchData();
  }, []);

  // Handle "See All Blogs" click from footer
  const handleSeeAllBlogs = () => {
    setShowAllBlogs(true);
    // Scroll to blog section
    if (blogSectionRef.current) {
      blogSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="homepage-container">
      {/* Navigation: Top bar for easy access—scalable with more links as site grows. */}
      <nav className="navigation">
        <ul>
          <li><a href="/">Home</a></li>
          <li><a href="/products">Products</a></li>
          <li><a href="/register">Register/Login</a></li>
          <li><a href="/contact">Contact</a></li>
        </ul>
      </nav>

      {/* Top: Billboard header for announcements—dynamic for real-time updates. */}
      <header className="billboard">
        {announcements.map(ann => (
          <div key={ann.id} className="announcement">
            <span>{ann.text}</span>
            {(ann.link_url || ann.product_id) && (
              <a 
                href={ann.link_url || `/products/${ann.product_id}`} 
                className="announcement-link"
              >
                {ann.link_text || 'Learn more →'}
              </a>
            )}
          </div>
        ))}
      </header>

      {/* Middle: Publisher's bio, mission, and vision—static text blocks for now; make editable via Django admin. */}
      <section className="publisher-info">
        <div className="bio">
          <h2>Publisher's Bio</h2>
          <p>ChristensenKirtis is a dedicated publisher focused on innovative digital content...</p>
        </div>
        <div className="mission">
          <h2>Mission</h2>
          <p>To inspire and educate through timely ebooks, audiobooks, games, and podcasts.</p>
        </div>
        <div className="vision">
          <h2>Vision</h2>
          <p>Building a scalable platform that grows with creators and audiences worldwide.</p>
        </div>
      </section>

      {/* Blog section: Grid/list of short posts—fetched dynamically; auto-push handled in Django backend. */}
      <section className="blog-section" ref={blogSectionRef}>
        <div className="blog-section-header">
          <h2>Latest Blog Posts</h2>
          {!showAllBlogs && blogPosts.length > 3 && (
            <button className="see-all-btn" onClick={handleSeeAllBlogs}>
              See All Blogs ({blogPosts.length})
            </button>
          )}
          {showAllBlogs && (
            <button className="see-all-btn" onClick={() => setShowAllBlogs(false)}>
              Show Less
            </button>
          )}
        </div>
        <div className="blog-grid">
          {(showAllBlogs ? blogPosts : blogPosts.slice(0, 3)).map(post => (
            <Link 
              key={post.id} 
              to={`/blog/${post.slug}`}
              className="blog-post"
              title="Click to read full article"
            >
              {post.featured_image_url && (
                <img src={post.featured_image_url} alt={post.title} className="blog-thumbnail" />
              )}
              <h3>{post.title}</h3>
              <p>{post.excerpt}</p>
              <span className="blog-meta">
                {post.category_display} • {post.published_at ? new Date(post.published_at).toLocaleDateString() : post.date}
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Footer: Social shares and highlights—configurable for growth. */}
      <footer className="footer">
        <div className="footer-nav">
          <div className="footer-section">
            <h4>Quick Links</h4>
            <ul>
              <li><a href="/">Home</a></li>
              <li><a href="/products">Products</a></li>
              <li><a href="#blogs" onClick={(e) => { e.preventDefault(); handleSeeAllBlogs(); }}>Blogs</a></li>
              <li><a href="/contact">Contact</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Connect With Us</h4>
            <div className="social-shares">
              <a href="https://x.com/ChristnsnKirtis" target="_blank" rel="noopener noreferrer">
                <span className="social-icon">𝕏</span> X (Twitter)
              </a>
              <a href="https://facebook.com/christensenkirtis" target="_blank" rel="noopener noreferrer">
                <span className="social-icon">f</span> Facebook
              </a>
            </div>
          </div>
          <div className="footer-section">
            <h4>Highlights</h4>
            <p>Free samples available on all products!</p>
            <p>Subscribe to our newsletter for updates.</p>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 ChristensenKirtis Publishing. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;